import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LOCATIONS from '../constants/locations';
import PhotoUploader from '../components/PhotoUploader';
import QuestionPicker from '../components/QuestionPicker';
import PageHeader from '../components/ui/PageHeader';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';

export default function CreateListing() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', description: '', category: 'Cleaning',
    price: '', location: '', cover_photo_url: '', photos: []
  });
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        const { location } = res.data.user;
        if (location) setForm(f => ({ ...f, location }));
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/listings`,
        { ...form, price: form.price ? parseFloat(form.price) : null, questions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/company/listings');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-sand-50 min-h-screen pb-24 sm:pb-0">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <PageHeader title="List a Service" subtitle="Fill in the details and publish your listing" />

        <form id="create-listing-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
          <Input
            id="title"
            label="Title"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Professional deep cleaning"
            required
          />
          <div>
            <Input
              id="description"
              label="Description"
              as="textarea"
              rows={5}
              value={form.description}
              onChange={e => {
                const val = e.target.value;
                const words = val.trim() === '' ? 0 : val.trim().split(/\s+/).length;
                if (words <= 100) setForm({ ...form, description: val });
              }}
              placeholder="Describe your service in detail…"
              className="resize-y"
            />
            <p className={`text-xs mt-1 text-right ${
              (form.description.trim() === '' ? 0 : form.description.trim().split(/\s+/).length) >= 100
                ? 'text-uae-red'
                : 'text-ink-400'
            }`}>
              {form.description.trim() === '' ? 0 : form.description.trim().split(/\s+/).length} / 100 words
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              id="price"
              label="Price (AED)"
              type="number"
              min="0"
              value={form.price}
              onChange={e => setForm({ ...form, price: e.target.value })}
              placeholder="0"
            />
            <div>
              <label className="text-sm font-medium text-ink-700 block mb-1">Location</label>
              <select
                value={form.location}
                onChange={e => setForm({ ...form, location: e.target.value })}
                className="w-full px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 transition-all duration-200"
              >
                <option value="">Select…</option>
                {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>

          {/* Cover photo */}
          <div>
            <label className="text-sm font-medium text-ink-700 block mb-2">Cover Photo</label>
            {form.cover_photo_url
              ? <div className="relative">
                  <img src={form.cover_photo_url} alt="cover" className="w-full h-48 object-cover rounded-md" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, cover_photo_url: '' })}
                    className="absolute top-2 right-2 bg-white text-ink-700 text-xs px-2 py-1 rounded shadow-card"
                  >
                    Remove
                  </button>
                </div>
              : <PhotoUploader
                  label="Upload Cover"
                  onUpload={url => setForm({ ...form, cover_photo_url: url })}
                />
            }
          </div>

          {/* Additional photos */}
          <div>
            <label className="text-sm font-medium text-ink-700 block mb-2">
              Additional Photos ({form.photos.length}/5)
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {form.photos.map((url, i) => (
                <div key={i} className="relative">
                  <img src={url} alt="" className="w-full h-24 object-cover rounded-md" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })}
                    className="absolute top-1 right-1 bg-white text-ink-700 text-xs px-1.5 py-0.5 rounded shadow-card"
                  >
                    ×
                  </button>
                </div>
              ))}
              {form.photos.length < 5 &&
                <PhotoUploader
                  label="+ Add photo"
                  onUpload={url => setForm({ ...form, photos: [...form.photos, url] })}
                />
              }
            </div>
          </div>

          {/* Questions asked at order time */}
          <div>
            <label className="text-sm font-medium text-ink-700 block mb-2">
              Questions to ask customers (optional)
            </label>
            <p className="text-xs text-ink-400 mb-3">
              Pick from the question bank. Customers will fill these in when they place an order.
            </p>
            <QuestionPicker value={questions} onChange={setQuestions} />
          </div>

          {error && <p className="text-sm text-uae-red">{error}</p>}

          <div className="hidden sm:block">
            <Button type="submit" variant="primary" loading={loading} className="w-full justify-center">
              Publish Listing
            </Button>
          </div>
        </form>
      </div>

      {/* Mobile fixed submit */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-sand-200 p-4 sm:hidden z-30">
        <Button type="submit" form="create-listing-form" variant="primary" loading={loading} className="w-full justify-center">
          Publish Listing
        </Button>
      </div>
    </div>
  );
}
